/** Supplier statements: file an inbox document as a statement (not a cost), or move it back. */
import { useState } from 'react';
import { toast } from 'sonner';
import { FileStack, Loader2, Undo2, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';

export function StatementBanner({ api, item, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!item || item.status !== 'statement') return null;
  const unfile = async () => {
    setBusy(true);
    try { await api.post(`/cost-inbox/${item.id}/statement`, { statement: false }); toast.success('Moved back to the inbox for processing'); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not move it back'); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-violet-400/40 bg-violet-500/10 p-2.5 text-xs flex items-start gap-2" data-testid="inbox-statement-banner">
      <FileStack className="w-4 h-4 mt-0.5 shrink-0 text-violet-300" />
      <div className="min-w-0 text-gray-200 flex-1">
        <span className="font-semibold text-white">Supplier statement</span> — filed{item.statement_at ? ` ${new Date(item.statement_at).toLocaleDateString('en-GB')}` : ''}{item.statement_by_name ? ` by ${item.statement_by_name}` : ''}. Statements are kept for reference and never posted as a cost.
      </div>
      <button type="button" onClick={unfile} disabled={busy} className="inline-flex items-center gap-1 underline text-violet-200 hover:text-white disabled:opacity-50 shrink-0" data-testid="inbox-statement-unfile-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />} Not a statement
      </button>
    </div>
  );
}

export function MarkStatementButton({ api, item, supplierName, beforeMark, onMarked, busy: outerBusy }) {
  const [busy, setBusy] = useState(false);
  if (!item || !['new', 'in_review', 'on_hold'].includes(item.status)) return null;
  const run = async () => {
    setBusy(true);
    try {
      await beforeMark?.();
      await api.post(`/cost-inbox/${item.id}/statement`, { statement: true, supplier_name: supplierName || null });
      toast.success(`Filed under Statements${supplierName ? ` · ${supplierName}` : ''}`);
      onMarked?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not file as a statement'); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="outline" onClick={run} disabled={busy || outerBusy} title="File this document as a supplier statement — kept for reference, not posted as a cost"
      className="border-violet-400/40 text-violet-200 bg-violet-500/10 hover:bg-violet-500/20" data-testid="inbox-mark-statement-btn">
      {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileStack className="w-4 h-4 mr-1.5" />} Statement
    </Button>
  );
}

export function StatementHint({ item }) {
  if (!item || item.status !== 'new' && item.status !== 'in_review') return null;
  if ((item.extraction?.document_type || '') !== 'statement') return null;
  return (
    <div className="text-[11px] text-violet-200 flex items-start gap-1.5 bg-violet-500/10 border border-violet-500/25 rounded px-2 py-1" data-testid="inbox-statement-hint">
      <Sparkles className="w-3 h-3 mt-0.5 shrink-0" /> The reader thinks this is a supplier <b className="mx-1">statement</b>, not an invoice — use the Statement button below to file it.
    </div>
  );
}
